import type { CdwGridFilter } from '@cdw-ae/common-data';
import { DataHelper } from '@cdw-ae/common-frontend-data';
import type {
  FilterBarField,
  SelectedFilterField,
} from '@cdw-ae/filter-bar-interfaces';

export interface HydratedSnapshot {
  restored: SelectedFilterField[];
  /** Count of payload entries whose field/matcher no longer exists in settings. */
  skipped: number;
}

/**
 * Rebuilds `SelectedFilterField[]` from a snapshot payload by **name lookup**
 * against the route's current filter settings: `field` by name, then
 * `selectedMatcher` by name within that field's `fieldMatchers`, keeping
 * `matchValue` and minting a fresh `uid` (EDUS-2575 §5).
 *
 * Deliberately does NOT route through the URL `convertToInput`/operator maps —
 * those lack `multiTextSearch`/`multiContains` entries and would corrupt our
 * target filters.
 *
 * **Partial restore on config drift:** snapshots are permanent but settings
 * evolve. Entries whose field/matcher can't be resolved are **skipped** (not
 * fatal) and counted, so a shared link never crashes — it restores what it can.
 */
export function hydrateFilterSnapshot(
  payload: CdwGridFilter[] | undefined,
  filterSettings: FilterBarField[]
): HydratedSnapshot {
  const restored: SelectedFilterField[] = [];
  let skipped = 0;

  for (const entry of payload ?? []) {
    const field = filterSettings.find((f) => f.name === entry.field?.name);
    const matcher = field?.fieldMatchers.find(
      (m) => m.name === entry.selectedMatcher?.name
    );
    if (!(field && matcher)) {
      skipped++;
      continue;
    }
    restored.push({
      uid: DataHelper.makeUUID(),
      field,
      selectedMatcher: matcher,
      matchValue: entry.matchValue,
    });
  }

  return { restored, skipped };
}
